// src/components/AICoach.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, Sparkles, Loader2, BarChart3, ChevronRight, TrendingUp, AlertCircle, HeartPulse, ClipboardList, Eraser } from 'lucide-react';
import CoachSupportCard from './coach/CoachSupportCard';
import SupportOutlet from './dashboard/SupportOutlet';
import { detectCrisisLanguage, detectSupportThemes } from '../utils/crisisDetect';
import { setPendingWorksheet } from '../utils/guidedPath';
import { worksheetsForTopic } from '../data/worksheets';
import ReactMarkdown from 'react-markdown';
// FIREBASE IMPORTS. The conversation itself is written by the server (coachGateway):
// the client never writes session_context, it only asks the gateway to clear it.
import { functions } from '../config/firebase-config';
import { httpsCallable } from 'firebase/functions';

// --- CONFIGURATIONS ---
export const MAIN_AGENT_CONFIG = {
    name: "AI Coach",
    role: "Wellness & Performance Coach",
    welcome: "Hi, I'm your AI Coach. This chat is private to you: it never goes to your coach, your team or your organization. What's on your mind?",
    style: "bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 shadow-indigo-500/20"
};

// The first thing the coach says, by who is listening. Decided 2026-10-13: the
// name stays "AI Coach"; the opening line makes two things unmistakable, that it
// is not a person and that nothing said here reaches the member's real coach.
export const welcomeFor = (ctx) => {
    const persona = ctx?.params?.persona || 'athlete';
    const org = ctx?.params?.org_name ? ` in the ${ctx.params.org_name} app` : '';
    switch (persona) {
        case 'youth':
            if (ctx?.params?.org_sport === 'school') return `Hey! I'm the AI Coach${org}. I'm not a person, and I'm not a teacher: nothing you say here goes to your teachers, your parents or anyone at your school. It's just for you. I'm good at helping you think things through, like sleep, stress, a tough test, friends, or anything else. What's up today?`;
            return `Hey! I'm the AI Coach${org}. I'm not a person, and I'm not your real coach: nothing you say here goes to your coach, your team, your parents or anyone at your organization. It's just for you. I'm good at helping you think things through, like sleep, stress, a tough game, or anything else. What's up today?`;
        case 'parent':
            return `Hi, I'm the AI Coach${org}. I can help with how to talk to your player and what to watch for. I can't see your player's answers, and nothing here goes to the team or the organization. Where would you like to start?`;
        case 'coach':
            return `Hi, I'm the AI Coach${org}. I work at the team level and never with an individual player's results. This chat is private to you. How can I help?`;
        case 'employee':
            return `Hi, I'm your AI Coach. This chat is private to you: it never goes to your manager or your organization. I can help with sleep, stress, focus and the busy weeks. What's on your mind?`;
        case 'family':
            return `Hi, I'm your AI Coach. This chat is private to you and never goes to your organization. I'm here for your own wellbeing and for the caregiving side. What's on your mind?`;
        case 'staff':
            return `Hi, I'm the AI Coach. I can explain how the platform and its resources work, and talk through general wellbeing. I never see any individual member's results. How can I help?`;
        case 'responder':
            return `Hi, I'm your AI Coach${org}. This chat is private to you: it never goes to your supervisor, your unit or the service. I can help with sleep on a rotation, the weeks after a hard call, focus and home. Keep case details and names out of it. What's on your mind?`;
        default:
            return `Hi, I'm your AI Coach${org}. This chat is private to you: it never goes to your coach, your team or your organization. I can help with sleep, stress, focus and the big moments. What's on your mind?`;
    }
};

export const ANALYTICS_AGENT_CONFIG = {
    name: "Data Analyst",
    role: "Team Insights Expert",
    welcome: "I'm analyzing your team's usage data. Ask me about engagement trends, popular topics, or risk flags.",
    style: "bg-slate-900"
};

const newSessionId = () => `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const AICoach = ({ isActive, agentConfig = MAIN_AGENT_CONFIG, navigateTo, externalPrompt, onPromptHandled, onCloseChat, insights, journalContext = null, scores = null, currentUser = null, coachContext = null }) => {
    // One conversation per panel mount; "Clear" starts a fresh one.
    const [sessionId, setSessionId] = useState(newSessionId);
    const [messages, setMessages] = useState(() => [
        { id: 1, text: welcomeFor(coachContext), sender: 'ai', timestamp: new Date() },
        // Safety flag from the member's own answers: real support first, before any chat
        ...(scores?.safetyFlag ? [{ id: 2, sender: 'support', reason: 'flag', timestamp: new Date() }] : [])
    ]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    // Support themes already answered this session (one card per theme).
    const shownThemesRef = useRef(new Set());

    // --- NEW: INSIGHTS TOGGLE ---
    const [showInsights, setShowInsights] = useState(true);

    // The welcome follows the persona: when the context resolves after mount (org
    // load, preview switch) and nobody has spoken yet, swap the opening line.
    const welcomeText = welcomeFor(coachContext);
    useEffect(() => {
        setMessages(prev => (prev.length === 1 && prev[0].sender === 'ai' && prev[0].text !== welcomeText)
            ? [{ ...prev[0], text: welcomeText }]
            : prev);
    }, [welcomeText]);

    // --- UI LOGIC ---
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, showInsights]); 

    // --- LOGIC: THE "BRAIN" (CALLABLE) ---
    // coachGateway (functions/modules/coachGateway.js) decides who is talking from
    // the user and org docs; the client sends the summary it already displays,
    // the opt-in journal summary, and a crisis flag from its own language check.
    const generateResponse = useCallback(async (userText, crisis = false) => {
        setIsTyping(true);
        try {
            const coachGateway = httpsCallable(functions, 'coachGateway');
            const result = await coachGateway({
                message: userText,
                sessionId,
                // Only present when the member opted in on My Journal
                ...(journalContext ? { journalContext } : {}),
                // Whole-person context (both dimensions, safety, pulse, path step, voice)
                ...(coachContext ? { userContext: coachContext.summary, contextParams: coachContext.params } : {}),
                ...(crisis ? { crisisFlag: true } : {})
            });
            setIsTyping(false);
            return result.data?.text || "I'm having trouble finding the words right now. Try me again in a moment.";
        } catch (error) {
            console.error("AI Coach Error:", error);
            setIsTyping(false);
            if (error?.code === 'functions/failed-precondition') return error.message;
            return "I couldn't reach the coach service just now. Please try again in a moment.";
        }
    }, [sessionId, journalContext, coachContext]);

    // "Clear": the server deletes the stored conversation, the panel starts over.
    const clearConversation = useCallback(async () => {
        try { await httpsCallable(functions, 'coachGateway')({ sessionId, clear: true }); } catch (e) { /* nothing to clear */ }
        shownThemesRef.current = new Set();
        setSessionId(newSessionId());
        setMessages([{ id: Date.now(), text: welcomeText, sender: 'ai', timestamp: new Date() }]);
    }, [sessionId, welcomeText]);

    const handleSendMessage = useCallback(async (text) => {
        if (!text.trim()) return;

        const userMsg = { id: Date.now(), text: text, sender: 'user', timestamp: new Date() };
        const crisis = detectCrisisLanguage(text);
        // Specific-service themes (bullying, violence at home, substance use...)
        // get their own lines, once per theme per session so the card keeps meaning.
        const themes = crisis ? [] : detectSupportThemes(text).filter(t => !shownThemesRef.current.has(t));
        themes.forEach(t => shownThemesRef.current.add(t));
        // The crisis card is the safety floor: it shows at once, before the network.
        // Theme cards follow the coach's reply, so the exchange reads as an answer
        // first and the lines second (Cody, 2026-10-09).
        const crisisCard = crisis ? { id: Date.now() + 0.5, sender: 'support', reason: 'message', timestamp: new Date() } : null;
        setMessages(crisisCard ? [...messages, userMsg, crisisCard] : [...messages, userMsg]);
        setInputValue("");

        const responseText = await generateResponse(text, crisis);
        const aiMsg = { id: Date.now() + 1, text: responseText, sender: 'ai', timestamp: new Date() };
        const themeCard = themes.length > 0 ? { id: Date.now() + 1.5, sender: 'support', reason: 'theme', themes, timestamp: new Date() } : null;
        setMessages(prev => (themeCard ? [...prev, aiMsg, themeCard] : [...prev, aiMsg]));
    }, [generateResponse, messages]);

    // Handle External Prompts
    useEffect(() => {
        if (externalPrompt) {
            handleSendMessage(externalPrompt);
            if (onPromptHandled) onPromptHandled();
        }
    }, [externalPrompt, handleSendMessage, onPromptHandled]);

    return (
        <div className="flex flex-col h-full bg-slate-50 border-l border-slate-200 overflow-hidden">
            
            {/* HEADER */}
            <div className={`p-4 shrink-0 flex items-center justify-between shadow-md text-white ${agentConfig.style}`}>
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm border border-white/10">
                        {agentConfig.name.includes("Analyst") ? <BarChart3 size={20} className="text-white" /> : <Sparkles size={20} className="text-yellow-300" />}
                    </div>
                    <div>
                        <h2 className="font-bold text-sm leading-tight tracking-wide">{agentConfig.name}</h2>
                        <p className="text-[10px] opacity-90 uppercase tracking-wider font-medium text-white/80">{agentConfig.role}</p>
                    </div>
                </div>
                <button type="button" onClick={clearConversation} disabled={isTyping || messages.length <= 1} title="Clear this conversation" aria-label="Clear this conversation"
                    className="bg-black/20 hover:bg-black/30 disabled:opacity-40 px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1.5 border border-white/10 transition-colors">
                    <Eraser size={12} /> CLEAR
                </button>
            </div>

            {/* --- PERFORMANCE SNAPSHOT (2-COLUMN GRID) --- */}
            {insights && (insights.high?.length > 0 || insights.low?.length > 0) && (
                <div className="bg-white border-b border-slate-200 shrink-0 flex flex-col"> 
                    <button 
                        onClick={() => setShowInsights(!showInsights)}
                        className="w-full flex items-center justify-between p-3 text-xs font-bold text-slate-500 hover:bg-slate-50 uppercase tracking-wider shrink-0"
                    >
                        <span className="flex items-center gap-2"><BarChart3 size={14}/> Your Snapshot</span>
                        <ChevronRight size={14} className={`transition-transform ${showInsights ? 'rotate-90' : ''}`}/>
                    </button>
                    
                    {showInsights && (
                        <div className="px-3 pb-3 animate-in slide-in-from-top-2">
                            <div className="grid grid-cols-2 gap-2">
                                
                                {/* LEFT COL: High Performing */}
                                <div>
                                    <div className="flex items-center gap-1 text-[9px] font-bold text-green-600 mb-1.5 uppercase tracking-tight">
                                        <TrendingUp size={10}/> Top Strengths
                                    </div>
                                    <div className="space-y-1.5">
                                        {insights.high.slice(0, 2).map(metric => (
                                            <div key={metric.id} className="flex flex-col gap-1.5 bg-green-50/50 border border-green-100 rounded-lg p-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-slate-700 truncate">{metric.label}</span>
                                                    <div className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[9px] font-black">{metric.score}%</div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-1">
                                                    <button 
                                                        onClick={() => handleSendMessage(`I want to maintain my high ${metric.label}.`)} 
                                                        className="px-1 py-1 bg-white border border-green-200 text-green-700 rounded text-[9px] font-bold hover:bg-green-50 transition-colors uppercase flex justify-center items-center"
                                                    >
                                                        Chat
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            if (navigateTo) navigateTo(metric.route);
                                                            if (onCloseChat) onCloseChat(); // Close agent on mobile
                                                        }} 
                                                        className="px-1 py-1 bg-green-600 text-white rounded text-[9px] font-bold hover:bg-green-700 transition-colors shadow-sm uppercase flex justify-center items-center"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* RIGHT COL: Areas to work on */}
                                <div>
                                    <div className="flex items-center gap-1 text-[9px] font-bold text-orange-600 mb-1.5 uppercase tracking-tight">
                                        <AlertCircle size={10}/> Areas to work on
                                    </div>
                                    <div className="space-y-1.5">
                                        {insights.low.slice(0, 2).map(metric => (
                                            <div key={metric.id} className="flex flex-col gap-1.5 bg-orange-50/50 border border-orange-100 rounded-lg p-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-bold text-slate-700 truncate">{metric.label}</span>
                                                    <div className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[9px] font-black">{metric.score}%</div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-1">
                                                    <button 
                                                        onClick={() => handleSendMessage(`Help me improve my ${metric.label}.`)} 
                                                        className="px-1 py-1 bg-white border border-orange-200 text-orange-700 rounded text-[9px] font-bold hover:bg-orange-50 transition-colors uppercase flex justify-center items-center"
                                                    >
                                                        Chat
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            if (navigateTo) navigateTo(metric.route);
                                                            if (onCloseChat) onCloseChat(); // Close agent on mobile
                                                        }} 
                                                        className="px-1 py-1 bg-orange-500 text-white rounded text-[9px] font-bold hover:bg-orange-600 transition-colors shadow-sm uppercase flex justify-center items-center"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* --- WELLBEING SNAPSHOT + ACTIONS (the other half of the dual-dimension model) --- */}
            {showInsights && scores?.hasData && (
                <div className="bg-white border-b border-slate-200 shrink-0 px-3 pb-2.5 pt-2 animate-in slide-in-from-top-2">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider"><HeartPulse size={14}/> Wellbeing</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${scores.wellbeingTier === 'Healthy' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : scores.wellbeingTier === 'At Risk' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{scores.wellbeingTier}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-black text-slate-800 mr-1">{scores.quadrant?.label}</span>
                        {[['Stress', scores.labels?.stress], ['Sleep', scores.labels?.sleep], ['Focus', scores.labels?.focus]].map(([k, v]) => (
                            <span key={k} className="text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">{k}: {v || '—'}</span>
                        ))}
                    </div>
                    {/* Actions follow the quadrant: Running Hot = slow down and realign
                        (not a crisis); Needs Support = support first; the safety flag
                        (clinical) is what surfaces crisis lines in the conversation. */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {scores.quadrant?.id === 'running_hot' && (
                            <button onClick={() => { navigateTo && navigateTo('reset-recharge'); if (onCloseChat) onCloseChat(); }} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold border border-brand-primary/30 bg-brand-light text-brand-primary hover:bg-white flex items-center gap-1"><HeartPulse size={12}/> Reset &amp; Recharge</button>
                        )}
                        {(scores.quadrant?.id === 'needs_support' || scores.labels?.stress === 'High') && worksheetsForTopic('anxiety').length > 0 && (
                            <button onClick={() => { setPendingWorksheet('anxiety_worksheet'); navigateTo && navigateTo('anxiety'); if (onCloseChat) onCloseChat(); }} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold border border-brand-primary/30 bg-brand-light text-brand-primary hover:bg-white flex items-center gap-1"><ClipboardList size={12}/> Anxiety worksheet</button>
                        )}
                        {scores.labels?.sleep === 'Poor' && worksheetsForTopic('sleep').length > 0 && (
                            <button onClick={() => { setPendingWorksheet('sleep_checklist'); navigateTo && navigateTo('sleep'); if (onCloseChat) onCloseChat(); }} className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold border border-brand-primary/30 bg-brand-light text-brand-primary hover:bg-white flex items-center gap-1"><ClipboardList size={12}/> Sleep checklist</button>
                        )}
                        <SupportOutlet currentUser={currentUser} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border flex items-center gap-1 ${scores.quadrant?.id === 'needs_support' || scores.safetyFlag ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100' : 'border-slate-200 bg-white text-slate-600 hover:border-brand-primary/40 hover:text-brand-primary'}`} />
                    </div>
                </div>
            )}

            {/* MESSAGES AREA */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 min-h-0">
                {messages.map((msg) => {
                    if (msg.sender === 'support') {
                        return <CoachSupportCard key={msg.id} currentUser={currentUser} reason={msg.reason} themes={msg.themes || null} topicId={msg.reason === 'flag' ? 'depression' : null} />;
                    }
                    const isAi = msg.sender === 'ai';
                    return (
                        <div key={msg.id} className={`flex ${isAi ? 'justify-start' : 'justify-end'}`}>
                            <div className={`flex gap-2 max-w-[85%] ${isAi ? 'flex-row' : 'flex-row-reverse'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1 shadow-sm border ${isAi ? 'bg-white border-slate-200 text-brand-primary' : 'bg-brand-primary border-brand-primary text-white'}`}>
                                    {isAi ? <Bot size={14} /> : <User size={14} />}
                                </div>
                                <div className={`p-3 rounded-2xl text-sm leading-relaxed shadow-sm ${isAi ? 'bg-white border border-slate-200 text-slate-700 rounded-tl-none' : 'bg-brand-primary text-white rounded-tr-none'}`}>
                                    <ReactMarkdown
                                        components={{
                                            strong: ({node, ...props}) => <span className="font-bold" {...props} />,
                                            h3: ({node, children, ...props}) => <h3 className="font-bold uppercase text-xs mt-4 mb-1 tracking-wider opacity-90" {...props}>{children}</h3>,
                                            p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                                            ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                            li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                            img: ({node, ...props}) => <img className="rounded-lg my-3 max-w-full h-auto border border-slate-200 shadow-sm" {...props} alt={props.alt || "Diagram"}/>,
                                        }}
                                    >
                                        {msg.text}
                                    </ReactMarkdown>
                                    <div className={`text-[9px] mt-1 opacity-50 ${isAi ? 'text-left' : 'text-right'}`}>
                                        {msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {isTyping && (
                    <div className="flex justify-start">
                        <div className="flex gap-2 max-w-[85%]">
                            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 mt-1">
                                <Bot size={14} className="text-brand-primary" />
                            </div>
                            <div className="bg-white border border-slate-200 p-3 rounded-2xl rounded-tl-none flex items-center gap-1">
                                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
                                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-75"></div>
                                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce delay-150"></div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* INPUT AREA */}
            <div className="p-4 bg-white border-t border-slate-200 shrink-0">
               <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(inputValue); }} className="flex items-center gap-2 bg-slate-100 p-2 rounded-xl focus-within:ring-2 focus-within:ring-brand-primary/50 transition-all border border-transparent focus-within:border-brand-primary">
                    <input 
                        type="text" 
                        className="flex-1 bg-transparent border-none outline-none text-sm px-2 text-slate-700 placeholder:text-slate-400"
                        placeholder="Ask me anything..." 
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                    />
                    <button type="submit" disabled={!inputValue.trim() || isTyping} className="p-2 bg-brand-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                        {isTyping ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                </form>
                <div className="text-[10px] text-center text-slate-400 mt-2">
                    AI can make mistakes. Conversations are private to you and cleared after 30 days.
                </div>
            </div>
        </div>
    );
};

export default AICoach;